import { createFileRoute } from '@tanstack/react-router'
import { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs'
import { PageHeader } from '../../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../../lib/section-icons'
import { Badge } from '../../../../components/ui/badge'
import { EmptyState } from '../../../../components/ui/empty-state'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table'
import { BillingInvoiceBadge } from '../../../../components/admin/BillingInvoiceBadge'
import { invoicePeriod } from '../../../../components/admin/BillingInvoicesTable'
import { formatCents } from '../../../../hooks/use-admin-billing'
import {
    billingLabel,
    billingQuantity,
    billingUnitPrice,
    useBillingConsumptions,
    useBillingInvoice,
    useBillingInvoices,
    useBillingSummary,
    type BillingInvoice,
    type BillingSummary,
} from '../../../../hooks/use-billing'

export const Route = createFileRoute('/$org/settings/billing/')({
    component: BillingPage,
})

/**
 * The customer's billing (spec 2026-10-05-15): what is due, the current
 * month's estimate, the past months and the invoices. Read-only: billing is
 * edited by platform admins. Owner and admin only (`billing.read`).
 */
function BillingPage() {
    const { org } = Route.useParams()
    const { t } = useTranslation('billing')
    const summary = useBillingSummary(org)
    const consumptions = useBillingConsumptions(org)
    const invoices = useBillingInvoices(org)
    // The labels come with the consumptions (the registry's), keyed by code.
    const labels = useMemo(() => {
        const map: Record<string, Record<string, string>> = {}
        for (const c of consumptions.data?.codes ?? []) map[c.code] = c.label
        return map
    }, [consumptions.data])

    const breadcrumbs = useMemo(
        () => [
            { title: t('common:nav.home'), path: `/${org}` },
            { title: t('settings:page.title'), path: `/${org}/settings` },
            { title: t('breadcrumb') },
        ],
        [org, t]
    )
    useBreadcrumbs(breadcrumbs)

    const data = summary.data
    const detail = data && data.billable ? data : undefined

    return (
        <div className="container mx-auto py-8 max-w-5xl space-y-6" data-testid="org-billing-page">
            <PageHeader icon={SECTION_ICONS.adminBilling} title={t('title')} description={t('subtitle')} />

            {summary.error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 text-destructive text-sm" data-testid="org-billing-error">
                    {t('load_error')}
                </div>
            )}
            {summary.isLoading && <div className="text-muted-foreground">{t('loading')}</div>}
            {data && !data.billable && (
                <EmptyState title={t('not_billable.title')} description={t('not_billable.description')} data-testid="org-billing-not-billable" />
            )}

            {detail && (
                <>
                    <IncreaseNotice summary={detail} />
                    <DueSection summary={detail} />
                    <EstimateSection summary={detail} />
                    <PastMonthsSection loading={consumptions.isLoading} months={consumptions.data?.data ?? []} labels={labels} />
                    <InvoicesSection org={org} loading={invoices.isLoading} invoices={invoices.data?.data ?? []} labels={labels} />
                </>
            )}
        </div>
    )
}

function Section({ title, testid, children, aside }: { title: string; testid: string; children: React.ReactNode; aside?: React.ReactNode }) {
    return (
        <section className="bg-card rounded-lg border border-border p-4 space-y-3" data-testid={testid}>
            <div className="flex items-center justify-between gap-3">
                <h2 className="text-base font-semibold">{title}</h2>
                {aside}
            </div>
            {children}
        </section>
    )
}

function IncreaseNotice({ summary }: { summary: BillingSummary }) {
    const { t, i18n } = useTranslation('billing')
    const inc = summary.increase
    if (!inc) return null
    return (
        <div className="rounded-lg border border-warning/40 bg-warning/10 p-4 space-y-2" data-testid="org-billing-increase" role="status">
            <div className="font-semibold">{t(`increase.title_${inc.kind}`, { month: inc.month.slice(0, 7) })}</div>
            <p className="text-sm text-muted-foreground">{t(`increase.help_${inc.kind}`)}</p>
            <ul className="text-sm space-y-1">
                {inc.lines.map((l) => (
                    <li key={l.code} data-testid={`org-billing-increase-${l.code}`}>
                        <span className="font-medium">{billingLabel(l.label, l.code, i18n.language)}</span>
                        {': '}
                        {t('increase.line', {
                            previous: billingQuantity(l.previous_quantity, l.divisor, l.billing_unit, i18n.language),
                            current: billingQuantity(l.quantity, l.divisor, l.billing_unit, i18n.language),
                            amount: formatCents(l.estimated_cents, i18n.language),
                        })}
                    </li>
                ))}
            </ul>
        </div>
    )
}

function DueSection({ summary }: { summary: BillingSummary }) {
    const { t, i18n } = useTranslation('billing')
    return (
        <Section title={t('due.title')} testid="org-billing-due">
            <div className="grid gap-4 sm:grid-cols-2">
                <div>
                    <div className="text-sm text-muted-foreground">{t('due.due')}</div>
                    <div className="text-2xl font-semibold tabular-nums" data-testid="org-billing-due-amount">
                        {formatCents(summary.due_cents ?? 0, i18n.language)}
                    </div>
                </div>
                <div>
                    <div className="text-sm text-muted-foreground">{t('due.overdue')}</div>
                    <div
                        className={`text-2xl font-semibold tabular-nums ${(summary.overdue_cents ?? 0) > 0 ? 'text-destructive' : ''}`}
                        data-testid="org-billing-overdue-amount"
                    >
                        {formatCents(summary.overdue_cents ?? 0, i18n.language)}
                    </div>
                </div>
            </div>
            <p className="text-xs text-muted-foreground">{t('due.terms', { days: summary.payment_terms_days ?? 30 })}</p>
        </Section>
    )
}

function EstimateSection({ summary }: { summary: BillingSummary }) {
    const { t, i18n } = useTranslation('billing')
    const est = summary.estimate
    const annual = summary.annual
    const badge = <Badge variant="info">{t('estimate.badge')}</Badge>
    if (!est) {
        return (
            <Section title={t('estimate.title')} testid="org-billing-estimate">
                <p className="text-sm text-muted-foreground">{t('estimate.none')}</p>
            </Section>
        )
    }
    return (
        <Section title={t('estimate.title_month', { month: est.month.slice(0, 7) })} testid="org-billing-estimate" aside={badge}>
            <p className="text-xs text-muted-foreground">
                {est.as_of_day ? t('estimate.as_of', { day: est.as_of_day }) : t('estimate.no_data')} {t('estimate.help')}
            </p>
            {annual ? (
                <>
                    <p className="text-sm" data-testid="org-billing-annual-invoice">
                        {t('estimate.annual_invoice', {
                            number: annual.invoice_number ?? t('invoices.no_number'),
                            from: annual.period_start.slice(0, 7),
                            months: annual.months_left,
                        })}
                    </p>
                    {annual.codes.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t('estimate.annual_none')}</p>
                    ) : (
                        <Table data-testid="org-billing-annual-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('estimate.column.item')}</TableHead>
                                    <TableHead className="text-right">{t('estimate.column.invoiced')}</TableHead>
                                    <TableHead className="text-right">{t('estimate.column.year_max')}</TableHead>
                                    <TableHead className="text-right">{t('estimate.column.overage')}</TableHead>
                                    <TableHead className="text-right">{t('estimate.column.overage_amount')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {annual.codes.map((c) => (
                                    <TableRow key={c.code} data-testid={`org-billing-annual-${c.code}`}>
                                        <TableCell>{billingLabel(c.label, c.code, i18n.language)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{billingQuantity(c.invoiced_quantity, c.divisor, c.billing_unit, i18n.language)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{billingQuantity(c.year_max, c.divisor, c.billing_unit, i18n.language)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{billingQuantity(c.estimated_overage_quantity, c.divisor, c.billing_unit, i18n.language)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{formatCents(c.estimated_overage_cents, i18n.language)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </>
            ) : est.lines.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('estimate.no_lines')}</p>
            ) : (
                <Table data-testid="org-billing-estimate-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('estimate.column.item')}</TableHead>
                            <TableHead className="text-right">{t('estimate.column.quantity')}</TableHead>
                            <TableHead className="text-right">{t('estimate.column.unit_price')}</TableHead>
                            <TableHead className="text-right">{t('estimate.column.amount')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {est.lines.map((l) => (
                            <TableRow key={l.code} data-testid={`org-billing-estimate-${l.code}`}>
                                <TableCell>{billingLabel(l.label, l.code, i18n.language)}</TableCell>
                                <TableCell className="text-right tabular-nums">{billingQuantity(l.quantity, l.divisor, l.billing_unit, i18n.language)}</TableCell>
                                <TableCell className="text-right tabular-nums">{billingUnitPrice(l.unit_price_micros, i18n.language, l.price_period)}</TableCell>
                                <TableCell className="text-right tabular-nums">{formatCents(l.amount_cents, i18n.language)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            <div className="flex justify-end gap-2 text-sm">
                <span className="text-muted-foreground">{annual ? t('estimate.total_overage') : t('estimate.total')}</span>
                <span className="font-semibold tabular-nums" data-testid="org-billing-estimate-total">
                    {formatCents(est.estimated_total_cents, i18n.language)}
                </span>
            </div>
        </Section>
    )
}

type Labels = Record<string, Record<string, string>>

function PastMonthsSection({ months, loading, labels }: { months: NonNullable<ReturnType<typeof useBillingConsumptions>['data']>['data']; loading: boolean; labels: Labels }) {
    const { t, i18n } = useTranslation('billing')
    return (
        <Section title={t('months.title')} testid="org-billing-months">
            {loading && <div className="text-muted-foreground text-sm">{t('loading')}</div>}
            {!loading && months.length === 0 && <p className="text-sm text-muted-foreground">{t('months.empty')}</p>}
            {months.length > 0 && (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('months.column.month')}</TableHead>
                            <TableHead>{t('months.column.detail')}</TableHead>
                            <TableHead className="text-right">{t('months.column.total')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {months.map((m) => (
                            <TableRow key={m.month} data-testid={`org-billing-month-${m.month.slice(0, 7)}`}>
                                <TableCell className="tabular-nums align-top">{m.month.slice(0, 7)}</TableCell>
                                <TableCell>
                                    <ul className="text-sm space-y-0.5">
                                        {m.lines.map((l) => (
                                            <li key={l.code} data-testid={`org-billing-month-line-${l.code}`}>
                                                <span className="font-medium">{billingLabel(labels[l.code], l.code, i18n.language)}</span>
                                                {' · '}
                                                <span className="tabular-nums text-muted-foreground">
                                                    {l.code === 'fixed_fee' ? '' : `${new Intl.NumberFormat(i18n.language).format(l.divisor > 1 ? l.quantity / l.divisor : l.quantity)} × ${billingUnitPrice(l.unit_price_micros, i18n.language)} = `}
                                                </span>
                                                <span className="tabular-nums">{formatCents(l.amount_cents, i18n.language)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </TableCell>
                                <TableCell className="text-right tabular-nums align-top">{formatCents(m.total_cents, i18n.language)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Section>
    )
}

function InvoicesSection({ org, invoices, loading, labels }: { org: string; invoices: BillingInvoice[]; loading: boolean; labels: Labels }) {
    const { t, i18n } = useTranslation('billing')
    const [open, setOpen] = useState<string | null>(null)
    return (
        <Section title={t('invoices.title')} testid="org-billing-invoices">
            {loading && <div className="text-muted-foreground text-sm">{t('loading')}</div>}
            {!loading && invoices.length === 0 && <p className="text-sm text-muted-foreground">{t('invoices.empty')}</p>}
            {invoices.length > 0 && (
                <div className="overflow-x-auto">
                <Table data-testid="org-billing-invoices-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('invoices.column.number')}</TableHead>
                            <TableHead>{t('invoices.column.period')}</TableHead>
                            <TableHead className="text-right">{t('invoices.column.total')}</TableHead>
                            <TableHead>{t('invoices.column.state')}</TableHead>
                            <TableHead className="text-right">{t('invoices.column.remaining')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {invoices.map((inv) => (
                            <Fragment key={inv.uid}>
                                <TableRow data-testid={`org-billing-invoice-${inv.uid}`}>
                                    <TableCell>
                                        <button
                                            type="button"
                                            className="text-primary hover:underline text-left"
                                            aria-expanded={open === inv.uid}
                                            onClick={() => setOpen(open === inv.uid ? null : inv.uid)}
                                            data-testid={`org-billing-invoice-toggle-${inv.uid}`}
                                        >
                                            {inv.number ?? t('invoices.no_number')}
                                        </button>
                                        {inv.kind !== 'monthly' && <span className="ml-2 text-xs text-muted-foreground">{t(`invoices.kind.${inv.kind}`)}</span>}
                                    </TableCell>
                                    <TableCell className="tabular-nums">{invoicePeriod(inv)}</TableCell>
                                    <TableCell className="text-right tabular-nums">{formatCents(inv.total_cents, i18n.language)}</TableCell>
                                    <TableCell>
                                        <BillingInvoiceBadge state={inv.payment_state} />
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums" data-testid="org-billing-invoice-remaining">
                                        {inv.status === 'issued' ? formatCents(inv.remaining_cents, i18n.language) : '—'}
                                    </TableCell>
                                </TableRow>
                                {open === inv.uid && (
                                    <TableRow>
                                        <TableCell colSpan={5} className="bg-muted/30">
                                            <InvoiceDetail org={org} uid={inv.uid} labels={labels} />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </Fragment>
                        ))}
                    </TableBody>
                </Table>
                </div>
            )}
        </Section>
    )
}

function InvoiceDetail({ org, uid, labels }: { org: string; uid: string; labels: Labels }) {
    const { t, i18n } = useTranslation('billing')
    const { data, isLoading, error } = useBillingInvoice(org, uid)
    if (isLoading) return <div className="text-sm text-muted-foreground">{t('loading')}</div>
    if (error || !data) return <div className="text-sm text-destructive">{t('load_error')}</div>
    return (
        <div className="space-y-3 text-sm" data-testid={`org-billing-invoice-detail-${uid}`}>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
                <span>{t('invoices.due_on')}: <span className="tabular-nums">{data.due_on ?? '—'}</span></span>
                <span>{t('invoices.excl_vat')}: <span className="tabular-nums">{formatCents(data.total_excl_vat_cents, i18n.language)}</span></span>
                <span>{t('invoices.vat', { rate: data.vat_rate_bp / 100 })}: <span className="tabular-nums">{formatCents(data.vat_cents, i18n.language)}</span></span>
                {data.credit_note_number && <span>{t('invoices.credit_note')}: {data.credit_note_number}</span>}
                {data.parent_number && <span>{t('invoices.parent', { number: data.parent_number })}</span>}
            </div>
            <ul className="space-y-0.5">
                {data.lines.map((l) => (
                    <li key={l.uid}>
                        {l.code === 'reduction' ? (l.label ?? t('invoices.reduction')) : billingLabel(labels[l.code], l.code, i18n.language)}
                        {' · '}
                        <span className="tabular-nums">{formatCents(l.amount_cents, i18n.language)}</span>
                    </li>
                ))}
            </ul>
            <div>
                <div className="font-medium">{t('invoices.payments')}</div>
                {data.payments.length === 0 ? (
                    <p className="text-muted-foreground">{t('invoices.no_payments')}</p>
                ) : (
                    <ul>
                        {data.payments.map((p, i) => (
                            <li key={i} className="tabular-nums">
                                {p.paid_on} · {formatCents(p.amount_cents, i18n.language)} · {t(`invoices.method.${p.method}`)}
                                {p.reference ? ` · ${p.reference}` : ''}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    )
}
